import {Alert, AlertIcon, AlertTitle} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React from 'react'
import * as Icons from '../../icons'
import {hasValidString, normalizeString} from '../../../utils/utils'

/**
 * Renders an icon with customizable properties.
 * Intended for use within the Leap components library.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {string} props.name - The icon identifier or name (e.g., emoji, icon library name).
 * @param {string} [props.title] - Optional title attribute for the icon.
 * @param {string} [props.alt] - Optional alt text for accessibility.
 * @returns {JSX.Element} An icon element.
 */

const Icon = ({name, title, alt}) => {
    const renderIcon = () => {
        if (!hasValidString(name)) return null

        // Convert icon name to component name (e.g., 'cart' -> 'CartIcon')
        const componentName =
            name
                .split('-')
                .map(normalizeString)
                .filter(Boolean)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                .join('') + 'Icon'

        // Try to get the icon component dynamically
        const IconComponent = Icons[componentName]

        if (IconComponent) {
            return <IconComponent title={title} aria-label={alt} />
        }

        // Show alert for unknown icon names
        return (
            <Alert status="warning" size="sm">
                <AlertIcon />
                <AlertTitle>Unknown icon: &quot;{name}&quot;</AlertTitle>
            </Alert>
        )
    }

    return renderIcon()
}

Icon.propTypes = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string,
    alt: PropTypes.string
}

export default Icon
